<script setup lang="ts">
import { onUnmounted, ref } from 'vue';

import {
  useBrowserStore,
  useEditingIngredientStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import {
  MapComponent,
  ProductSelectionMapLayout,
} from '@apps/main/new-desktop/modules/maps';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import type { Map, MapMovedEvent, StaticMap } from '@libs/maps';

import {
  useHotelSelectionStore,
  useHotelSelectionMapStore,
  useHotelSelectionModalsStore,
} from '../stores';
import { HotelSelectionModalType } from '../models';

import HotelMapCard from './HotelMapCard.vue';

interface HotelSelectionMapEmits {
  (event: 'close-map'): void;
  (event: 'open-filters'): void;
}
const emit = defineEmits<HotelSelectionMapEmits>();

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const statsigEventsStore = useStatsigEventsStore();
const mapStore = useMapStore();
const editingIngredientStore = useEditingIngredientStore();

const skipToggleSearchByMovingMap = ref(false);

onUnmounted(() => hotelSelectionMapStore.destroyMap());

function onHotelCardClicked(): void {
  statsigEventsStore.logHotelSelectionMapCardClicked({
    mapSize: hotelSelectionMapStore.hotelMapCardType,
    hotelCode: hotelSelectionMapStore.highlightedHotel?.hotelCode || '',
  });
  hotelSelectionMapStore.hotelCardClicked();
}

function onToggleSearchByMovingMap(): void {
  const newValue = !mapStore.searchByMapMovement;
  statsigEventsStore.logCoreSearchByMovingMapToggle(
    newValue,
    hotelSelectionMapStore.isFullScreen,
    {
      source: CoreEventSource.HotelSelection,
    }
  );
  hotelSelectionMapStore.toggleSearchByMovingMap();
}

function setMapIntegration(map: Map | StaticMap): void {
  hotelSelectionMapStore.setDynamicMapIntegration(map as Map);
}

function onToggleFullScreen(): void {
  skipToggleSearchByMovingMap.value = !skipToggleSearchByMovingMap.value;
  if (hotelSelectionMapStore.isFullScreen) {
    statsigEventsStore.logHotelSelectionCollapseMap();
  } else {
    statsigEventsStore.logHotelSelectionExtendMap();
  }

  hotelSelectionMapStore.onToggleFullScreen(
    !hotelSelectionMapStore.isFullScreen
  );
}

function onViewChanged(mapEvent: MapMovedEvent): void {
  // Skip map change event when toggling map size to avoid unnecessary room reloading
  if (skipToggleSearchByMovingMap.value) {
    skipToggleSearchByMovingMap.value = false;
    return;
  }
  hotelSelectionMapStore.mapViewChanged(mapEvent);
}

function onPeekPrice(): void {
  if (!hotelSelectionMapStore.hotelMapCardData) {
    return;
  }
  statsigEventsStore.logHotelSelectionPeekPriceModalOpened({
    source: 'mapMarker',
  });
  hotelSelectionModalsStore.openPeekPriceModal(
    hotelSelectionMapStore.hotelMapCardData.ingredientIndex,
    hotelSelectionMapStore.hotelMapCardData?.hotelCode
  );
}

function onHide(): void {
  if (
    hotelSelectionModalsStore.hotelSelectionModalType ===
    HotelSelectionModalType.PeekPrice
  ) {
    return;
  }
  hotelSelectionMapStore.hideHotelCard();
}
</script>

<template>
  <ProductSelectionMapLayout
    :should-display-loader="hotelSelectionStore.shouldDisplayLoader"
    :map-layout="browserStore.isLargerThan1199px ? 'desktop' : 'mobile'"
    :map-type="hotelSelectionMapStore.mapType"
    :is-desktop-map-full-screen="hotelSelectionMapStore.isFullScreen"
    :should-search-by-map-movement="mapStore.shouldSearchByMapMovement"
    :disable-should-search-by-map-movement="
      editingIngredientStore.selectedNeigborhoodFilters.length > 0
    "
    :active-filters-count="hotelSelectionStore.activeFiltersCount"
    @toggle-search-by-moving-map="onToggleSearchByMovingMap"
    @toggle-full-screen="onToggleFullScreen"
    @close-map="emit('close-map')"
    @open-filters="emit('open-filters')"
    @set-map-type="hotelSelectionMapStore.setMapType"
  >
    <template #map>
      <MapComponent
        :marker-formatters="
          hotelSelectionMapStore.mapProperties.markerFormatters
        "
        :cluster-marker-formatters="
          hotelSelectionMapStore.mapProperties.clusterMarkerFormatters
        "
        :cluster-options="hotelSelectionMapStore.mapProperties.clusterOptions"
        :markers="hotelSelectionMapStore.mapProperties.markers"
        :route="hotelSelectionMapStore.mapProperties.route"
        :show-all-markers="hotelSelectionMapStore.mapProperties.showAllMarkers"
        :start-rectangle="hotelSelectionMapStore.mapProperties.startRectangle"
        :gesture-handling="hotelSelectionMapStore.mapProperties.gestureHandling"
        :locale="hotelSelectionMapStore.mapProperties.locale"
        :map-controls="hotelSelectionMapStore.mapProperties.mapControls"
        :map-type="hotelSelectionMapStore.mapType"
        class="hotel-selection-map"
        @on-marker-clicked="hotelSelectionMapStore.markerClicked"
        @on-view-changed="onViewChanged"
        @map-created="setMapIntegration"
        @on-map-type-id-changed="hotelSelectionMapStore.setMapTypeId"
      />
    </template>

    <template #product-card>
      <HotelMapCard
        v-if="
          hotelSelectionMapStore.highlightedHotel?.showHotelMapCard &&
          hotelSelectionMapStore.hotelMapCardData
        "
        :data="hotelSelectionMapStore.hotelMapCardData"
        :card-type="hotelSelectionMapStore.hotelMapCardType"
        class="hotel-map-card"
        @click="onHotelCardClicked"
        @open-peek-price-calendar="onPeekPrice"
        @hide="onHide"
      />
    </template>
  </ProductSelectionMapLayout>
</template>

<style lang="scss">
.gm-style-mtc-bbw {
  right: auto !important;
  bottom: var(--spacing-12) !important;
  left: 0 !important;
  top: auto !important;
}

.hotel-selection-map {
  .hotel-selection-accommodation-marker {
    .hotel-selection-map-marker-label {
      @include font-label-m-bold;
    }

    cursor: pointer;

    padding: 6px var(--spacing-8);
    border: 1px solid var(--color-neutral-40);
    border-radius: var(--border-radius-8);
    background: var(--color-white);
    color: var(--color-neutral-70);
    box-shadow: var(--shadow-elevation-200);
    @include font-label-l-bold;
    transition: all var(--transition-short);
    @include on-hover {
      background-color: var(--color-neutral-30);
      border: 1px solid var(--color-neutral-40);
    }

    &.preselected {
      padding: 6px var(--spacing-8) 6px var(--spacing-32);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-white)
        url('../../../../assets/icon/scss/map/check-primary.svg')
        var(--spacing-8) center no-repeat;
      color: var(--color-neutral-70);
      box-shadow: var(--shadow-elevation-200);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-30);
        border: 2px solid var(--color-neutral-70);
      }
    }

    &.highlighted.preselected {
      padding: 6px var(--spacing-8) 6px var(--spacing-32);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-brand-violet)
        url('../../../../assets/icon/scss/map/check-white.svg') var(--spacing-8)
        center no-repeat;
      color: var(--color-white);
      box-shadow: var(--shadow-elevation-200);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-70);
        border: 2px solid var(--color-neutral-70);
      }
    }

    &.highlighted {
      padding: 6px var(--spacing-8);
      border: 2px solid var(--color-brand-violet);
      border-radius: var(--border-radius-8);
      background: var(--color-brand-violet);
      color: var(--color-white);
      box-shadow: var(--shadow-elevation-200);
      @include font-label-l-bold;
      transition: all var(--transition-short);
      @include on-hover {
        background-color: var(--color-neutral-70);
        border: 2px solid var(--color-neutral-70);
      }
    }

    @include for-less-or-equal-than-1200-px {
      padding: 5px var(--spacing-16);

      .hearted,
      .hearted.selected,
      .hearted.preselected {
        background-size: auto 18px;
        padding-left: 34px;
      }
    }
  }
  .trip-planner-hotel-marker {
    padding: 6px var(--spacing-16);
    cursor: pointer;
    border-radius: var(--border-radius-12);
    border: 1px solid var(--color-neutral-50);
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    filter: drop-shadow(0px 7px 14px rgba(0, 0, 0, 0.15));
    @include font-label-m-bold;
    @include on-hover {
      background-color: var(--color-neutral-30);
      z-index: 1;
    }

    &.selected,
    &.preselected {
      background-color: var(--color-neutral-70);
      color: var(--color-white);
      @include on-hover {
        background-color: var(--color-brand-violet);
      }
    }

    &.hearted,
    &.hearted.selected,
    &.hearted.preselected {
      background: var(--color-neutral-70)
        url('../../../../assets/icon/scss/map/check-white.svg')
        var(--spacing-16) center no-repeat;
      color: var(--color-white);
      padding-left: 40px;
      border: none;
      @include on-hover {
        background-color: var(--color-brand-violet);
      }
    }
    @include for-less-or-equal-than-1200-px {
      padding: 5px var(--spacing-16);

      .hearted,
      .hearted.selected,
      .hearted.preselected {
        background-size: auto 18px;
        padding-left: 34px;
      }
    }
  }
  @include for-more-or-equal-than-1200-px {
    .google-marker-container {
      @include on-hover {
        z-index: 9 !important;
      }
    }
    .google-marker-container.highlighted {
      z-index: 9 !important;
    }
  }

  @include map-zoom-control(34px, 40px, -40px, -80px);
  @include map-type-switch-vertical(var(--spacing-12));
}
</style>
